'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'DesignData/actions/History';
import PropTypes from 'prop-types';
import { bindAll, throttle } from 'underscore';
import I18n from 'I18n';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import { fetch as fetchSiteSettings } from 'DesignData/actions/SiteSettings';
import { fetch as fetchPreviewOptions } from 'DesignData/actions/PreviewOptions';
import { closeTabs } from 'DesignData/actions/Tabs';
import { toggleFinder as toggleFinderAction, setFinderWidth, setIsKeyboardShortcutsListingShowing } from 'DesignData/actions/Application';
import { toggleShouldShowStarterDowngradeNotice } from 'DesignData/actions/Onboarding';
import { fetch as fetchLayoutEditorOptions } from 'DesignData/actions/LayoutEditorOptions';
import { updateDragDropContextMonitor } from 'DesignData/actions/SharedDragDropContext';
import { fetchCustomWidgets, fetchMapping } from 'DesignData/actions/CustomWidgets';
import { fetchAttributesMeta } from 'DesignData/actions/Attributes';
import { fetchForWidgets as fetchGlobalGroupWidgets } from 'DesignData/actions/GlobalGroups';
import { fetchThemes } from 'DesignData/actions/finder/Themes';
import { fetchSummaries as fetchGlobalWidgets } from 'DesignData/actions/GlobalWidgets';
import { fetch as fetchCodeMirrorSession } from 'DesignData/actions/codemirror/Session';
import { fetchOpenFolders, fetchRootFolderContents } from 'DesignData/actions/finder/Folders';
import { openActionModal } from 'DesignData/actions/finder/Modals';
import { getHasFinishedFetching, getHasUsedCos, getHasUserOnboarded, shouldShowStarterDowngradeForPortal } from 'DesignData/selectors/onboarding';
import { getSkipOnboarding, getHasDndTemplateAccess } from 'DesignData/selectors/auth';
import { trackAppLoad, trackOpenContextMenu } from 'DesignManagerIde/utils/usageTracker/eventUtils';
import { getActiveAssetId, getIsDialog, getIsFinderShowing, getAlwaysShowFinderOnStartup, getIsKeyboardShortcutsListingShowing, getFinderWidth } from 'DesignData/selectors/app';
import { hasBufferedChanges, getShouldScrollActiveTabIntoView } from 'DesignData/selectors/tabs';
import Keybinding from 'DesignData/models/Keybinding';
import { KEYS, MODIFIERS } from 'DesignData/enums/Keys';
import { tryParseNumber, formatPathAsId } from 'DesignData/lib/text';
import Application from 'DesignManagerIde/components/Application';
import { tours } from 'DesignData/constants/Onboarding';
import { flash as flashAction } from 'DesignData/actions/Flash';
import { FOCUS_FINDER_SEARCH } from 'DesignData/enums/FlashKeys';
import PaneSize from 'DesignData/models/PaneSize';
import { fetchI18nInfo } from 'DesignData/actions/I18n';
import { MODAL_TYPES } from 'DesignData/enums/FinderActionModalTypes';
/**
 * @class
 */

class App extends Component {
  constructor(props) {
    super(props);

    this.onApplicationFocus = () => {
      if (document.visibilityState === 'visible') {
        this.fetchSettingsAndSessions();
      }
    };

    this.fetchSettingsAndSessions = throttle(function __fetchSettingsAndSessions() {
      const {
        portal_id
      } = this.props.auth.portal;
      this.props.fetchSiteSettings(portal_id);
      this.props.fetchCodeMirrorSession();
      this.props.fetchAttributesMeta();
      this.props.fetchLayoutEditorOptions();
      this.props.fetchPreviewOptions();
    }, 3000, {
      leading: true,
      trailing: false
    });

    this.closeKeyboardShortcutsListing = () => {
      if (this.props.isKeyboardShortcutsListingShowing) {
        this.props.setIsKeyboardShortcutsListingShowing(false);
      }
    };

    this.toggleKeyboardShortcutListing = () => {
      this.props.setIsKeyboardShortcutsListingShowing(!this.props.isKeyboardShortcutsListingShowing);
    };

    this.requestFinderSearchFocus = () => {
      const {
        isFinderShowing,
        toggleFinder,
        flash
      } = this.props;
      if (!isFinderShowing) toggleFinder(!isFinderShowing);
      flash(FOCUS_FINDER_SEARCH, true);
    };

    this.keybindingConfig = {
      listenOnDocument: true,
      [MODIFIERS.NONE]: {
        [KEYS.ESCAPE]: new Keybinding({
          handler: this.closeKeyboardShortcutsListing
        })
      },
      [MODIFIERS.COMMAND]: {
        [KEYS.P]: new Keybinding({
          handler: this.requestFinderSearchFocus
        })
      },
      [MODIFIERS.SHIFT]: {
        [KEYS.FORWARD_SLASH]: new Keybinding({
          handler: this.toggleKeyboardShortcutListing
        })
      }
    };
    this.state = {
      isContextMenuOpen: false,
      contextMenuCoordX: 0,
      contextMenuCoordY: 0,
      rightClickCoordX: 0,
      rightClickCoordY: 0,
      contextMenuContentData: null,
      contextMenuOptions: {},
      verticalOverflow: false,
      layoutAccessHasBeenChanged: false
    };
    bindAll(this, 'derefApplicationComponent', 'openContextMenu', 'closeContextMenu', 'setContextMenuLocation', 'swapContextMenuContent');
  }

  componentDidMount() {
    trackAppLoad();
    this.fetchSettingsAndSessions();
    this.props.fetchOpenFolders();
    this.props.fetchRootFolderContents();
    this.props.fetchMapping();
    this.props.fetchCustomWidgets();
    this.props.fetchGlobalGroupWidgets();
    this.props.fetchGlobalWidgets();
    this.props.fetchThemes();
    this.props.fetchI18nInfo();
    this.subscribeToDragAndDrop();
    this.bindDomEvents();
    document.title = I18n.text('designManager.pageTitle');
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.hasFinishedFetchingOnboarding && this.props.hasFinishedFetchingOnboarding) {
      /**
       * We use shouldShowStarterDowngradeNotice to determine three things
       * 1. If the user is on a tier above Starter. If they are, we set the property to true
       * 2. If the user was on a higher tier, and downgrades, we know to show them the plan downgrade modal
       * 3. If the user started out on a starter tier, we never set the property to true,
       *    so they never see the downgrade modal
       */
      if (this.props.hasDnDTemplateAccess && !this.props.shouldShowStarterDowngradeNotice) {
        this.props.toggleShouldShowStarterDowngradeNotice();
      } else if (!this.props.hasDnDTemplateAccess && this.props.shouldShowStarterDowngradeNotice) {
        this.props.openActionModal(MODAL_TYPES.PLAN_DOWNGRADE);
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribeDragAndDropListener();
    this.bindDomEvents(false);
  }

  subscribeToDragAndDrop() {
    const {
      store,
      monitor
    } = this.applicationComponent.getManager();
    this.unsubscribeDragAndDropListener = store.subscribe(() => {
      const itemType = monitor.getItemType();
      const isDragging = monitor.isDragging();
      const item = monitor.getItem();
      const current = this.props.dragDropContextMonitor;

      if (current.isDragging === isDragging && current.itemType === itemType && current.item === item) {
        return;
      }

      this.props.updateDragDropContextMonitor(isDragging, itemType, item);
    });
  }

  bindDomEvents(bind = true) {
    const fn = bind ? 'addEventListener' : 'removeEventListener';
    document[fn]('visibilitychange', this.onApplicationFocus);
    window[fn]('focus', this.onApplicationFocus);
  }
  /**
   * This fires when the application/browser/tab/window/desktop/etc is refocused.
   */


  derefApplicationComponent(app) {
    this.applicationComponent = app;
  }

  closeContextMenu() {
    const {
      contextMenuOptions,
      isContextMenuOpen
    } = this.state;
    const {
      onOpenChange
    } = contextMenuOptions || {};

    if (isContextMenuOpen) {
      this.setState({
        isContextMenuOpen: false,
        contextMenuContentData: null,
        contextMenuOptions: {}
      });
    }

    if (onOpenChange) onOpenChange(false);
  }

  openContextMenu(mouseClickEvent, menuContentData, menuOptions = {}) {
    mouseClickEvent.preventDefault();
    mouseClickEvent.stopPropagation();

    if (!menuContentData) {
      this.closeContextMenu();
      return;
    }

    const {
      onOpenChange
    } = menuOptions;
    trackOpenContextMenu();
    this.setState({
      isContextMenuOpen: true,
      contextMenuContentData: menuContentData,
      contextMenuOptions: menuOptions,
      rightClickCoordX: mouseClickEvent.clientX,
      rightClickCoordY: mouseClickEvent.clientY
    });
    if (onOpenChange) onOpenChange(true);
  }

  swapContextMenuContent(mouseClickEvent, content) {
    mouseClickEvent.preventDefault();
    mouseClickEvent.stopPropagation();

    if (!content) {
      this.closeContextMenu();
      return;
    }

    this.setState({
      contextMenuContentData: content
    });
  }

  setContextMenuLocation({
    x = 0,
    y = 0,
    verticalOverflow = false
  }) {
    this.setState({
      contextMenuCoordX: x,
      contextMenuCoordY: y,
      verticalOverflow
    });
  }

  getOnboardingTour() {
    const {
      hasFinishedFetchingOnboarding,
      hasUserOnboarded,
      hasUserUsedCos,
      skipOnboarding
    } = this.props;

    if (hasFinishedFetchingOnboarding && !skipOnboarding) {
      if (!hasUserUsedCos) {
        return tours.NEW_USER_IDE;
      }

      if (!hasUserOnboarded) {
        return tours.EXISTING_USER_IDE;
      }

      return null;
    }

    return undefined;
  }

  render() {
    const {
      state
    } = this;
    return /*#__PURE__*/_jsx(Application, {
      ref: this.derefApplicationComponent // State
      ,
      contextMenuContentData: state.contextMenuContentData,
      contextMenuCoordX: state.contextMenuCoordX,
      contextMenuCoordY: state.contextMenuCoordY,
      isContextMenuOpen: state.isContextMenuOpen,
      rightClickCoordX: state.rightClickCoordX,
      rightClickCoordY: state.rightClickCoordY,
      verticalOverflow: state.verticalOverflow // Props
      ,
      app: this.props.app,
      closeTabs: this.props.closeTabs,
      finderWidth: this.props.finderWidth,
      flash: this.props.flash,
      hasUserOnboarded: this.props.hasUserOnboarded,
      isFinderShowing: this.props.isFinderShowing,
      alwaysShowFinderOnStartup: this.props.alwaysShowFinderOnStartup,
      isKeyboardShortcutsListingShowing: this.props.isKeyboardShortcutsListingShowing,
      params: this.props.match.params,
      setFinderWidth: this.props.setFinderWidth,
      setIsKeyboardShortcutsListingShowing: this.props.setIsKeyboardShortcutsListingShowing,
      shouldScrollActiveTabIntoView: this.props.shouldScrollActiveTabIntoView,
      toggleFinder: this.props.toggleFinder,
      revisionId: this.props.revisionId,
      assetId: this.props.assetId // Components from Router
      ,
      mainComponent: this.props.mainComponent,
      finderComponent: this.props.finderComponent,
      settingsEditor: this.props.settingsEditor // Instance fields/methods
      ,
      closeContextMenu: this.closeContextMenu,
      openContextMenu: this.openContextMenu,
      setContextMenuLocation: this.setContextMenuLocation,
      onboardingTour: this.getOnboardingTour(),
      swapContextMenuContent: this.swapContextMenuContent,
      keybindingConfig: this.keybindingConfig
    });
  }

}

App.propTypes = {
  finderComponent: PropTypes.node,
  mainComponent: PropTypes.node,
  settingsEditor: PropTypes.node,
  app: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  fetchLayoutEditorOptions: PropTypes.func.isRequired,
  fetchPreviewOptions: PropTypes.func.isRequired,
  fetchSiteSettings: PropTypes.func.isRequired,
  closeTabs: PropTypes.func.isRequired,
  flash: PropTypes.func.isRequired,
  isFinderShowing: PropTypes.bool.isRequired,
  alwaysShowFinderOnStartup: PropTypes.bool.isRequired,
  toggleFinder: PropTypes.func.isRequired,
  setFinderWidth: PropTypes.func.isRequired,
  fetchCodeMirrorSession: PropTypes.func.isRequired,
  fetchAttributesMeta: PropTypes.func.isRequired,
  finderWidth: PropTypes.instanceOf(PaneSize),
  match: PropTypes.object.isRequired,
  shouldScrollActiveTabIntoView: PropTypes.bool.isRequired,
  skipOnboarding: PropTypes.bool.isRequired,
  assetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  revisionId: PropTypes.number,
  openActionModal: PropTypes.func.isRequired,
  // DnD Downgrade
  hasDnDTemplateAccess: PropTypes.bool.isRequired,
  shouldShowStarterDowngradeNotice: PropTypes.bool.isRequired,
  toggleShouldShowStarterDowngradeNotice: PropTypes.func.isRequired,
  // Keyboard shortcuts listing
  isKeyboardShortcutsListingShowing: PropTypes.bool.isRequired,
  setIsKeyboardShortcutsListingShowing: PropTypes.func.isRequired,
  fetchOpenFolders: PropTypes.func.isRequired,
  fetchRootFolderContents: PropTypes.func.isRequired,
  // 4 Widget Catalog getters
  fetchMapping: PropTypes.func.isRequired,
  fetchCustomWidgets: PropTypes.func.isRequired,
  fetchGlobalGroupWidgets: PropTypes.func.isRequired,
  fetchGlobalWidgets: PropTypes.func.isRequired,
  // Themes
  fetchThemes: PropTypes.func.isRequired,
  // Shared DND Context
  dragDropContextMonitor: PropTypes.object.isRequired,
  updateDragDropContextMonitor: PropTypes.func.isRequired,
  // Event tracking
  hasFinishedFetchingOnboarding: PropTypes.bool.isRequired,
  hasUserOnboarded: PropTypes.bool.isRequired,
  hasUserUsedCos: PropTypes.bool.isRequired,
  // I18n
  fetchI18nInfo: PropTypes.func.isRequired
};
App.contextType = TourContext;

const mapStateToProps = (state, props) => {
  const revisionId = tryParseNumber(props.match.params.revisionId);
  let assetId;

  if (props.match.params.assetId) {
    assetId = tryParseNumber(props.match.params.assetId);
  } else if (props.match.params[0]) {
    assetId = formatPathAsId(props.match.params[0]);
  }

  assetId = assetId || getActiveAssetId(state);
  return Object.assign({}, state, {
    assetId,
    revisionId,
    isDialog: getIsDialog(state),
    isFinderShowing: getIsFinderShowing(state),
    alwaysShowFinderOnStartup: getAlwaysShowFinderOnStartup(state),
    isKeyboardShortcutsListingShowing: getIsKeyboardShortcutsListingShowing(state),
    hasBufferedChanges: hasBufferedChanges(state),
    dragDropContextMonitor: state.sharedDragDropContext.monitor,
    hasFinishedFetchingOnboarding: getHasFinishedFetching(state),
    hasUserUsedCos: getHasUsedCos(state),
    hasUserOnboarded: getHasUserOnboarded(state),
    shouldScrollActiveTabIntoView: getShouldScrollActiveTabIntoView(state),
    finderWidth: getFinderWidth(state),
    skipOnboarding: getSkipOnboarding(state),
    hasDnDTemplateAccess: getHasDndTemplateAccess(state),
    shouldShowStarterDowngradeNotice: shouldShowStarterDowngradeForPortal(state)
  });
};

const mapDispatchToProps = {
  fetchLayoutEditorOptions,
  fetchPreviewOptions,
  fetchSiteSettings,
  closeTabs,
  push,
  toggleFinder: toggleFinderAction,
  setFinderWidth,
  setIsKeyboardShortcutsListingShowing,
  flash: flashAction,
  fetchCodeMirrorSession,
  fetchAttributesMeta,
  fetchRootFolderContents,
  fetchOpenFolders,
  openActionModal,
  getHasDndTemplateAccess,
  fetchThemes,
  // Widget Catalog
  fetchCustomWidgets,
  fetchMapping,
  fetchGlobalGroupWidgets,
  fetchGlobalWidgets,
  toggleShouldShowStarterDowngradeNotice,
  // Shared DND Context
  updateDragDropContextMonitor,
  // i18n
  fetchI18nInfo
};
export default connect(mapStateToProps, mapDispatchToProps)(App);